/**
 * Base de coste del usuario agregada en backend desde `pension_plan_contributions`
 * (solo CONFIRMED). Sirve para corregir el rendimiento de planes traspasados, donde
 * `daily_user_epp_details.total_contributions` no incluye el traspaso.
 *
 * Importes en EUROS.
 */
export interface UserCostBasisData {
	externalTransferCostBasis: number;
	totalRescued: number;
	totalParticipantContributions: number;
	totalCompanyContributions: number;
	/**
	 * `true` si el usuario tiene plan individual REAL en Arca (multi-plan). En ese caso
	 * NO debe aplicarse este cost-basis al rendimiento de empleo: el traspaso individual
	 * se ficha bajo el `company_id` del empleador y mezclaría los planes. Lo decide el
	 * backend (`caser_individual_plan_selected`), no una encuesta de onboarding.
	 */
	hasIndividualPlanSelected: boolean;
}

/**
 * ¿Aplicar el cost-basis externo al rendimiento de empleo? Solo si hay datos y el usuario
 * NO es multi-plan real (si lo es, mezclaríamos su traspaso individual con el de empleo).
 *
 * Conservador a propósito (`=== false`, no `!flag`): si el flag no llega exactamente como
 * `false` (p. ej. `undefined` con un backend antiguo desplegado antes que este front), NO
 * aplicamos — preferimos no corregir a arriesgarnos a mezclar planes de un multi-plan.
 */
export const shouldApplyExternalCostBasis = (
	costBasis: UserCostBasisData | null
): costBasis is UserCostBasisData =>
	costBasis !== null && costBasis.hasIndividualPlanSelected === false;

const getUserCostBasis = async (
	token: string,
	productCodes?: number[]
): Promise<UserCostBasisData> => {
	const response = await fetch(`${process.env.REACT_APP_BASE_API}/calculators/user-cost-basis`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`
		},
		// Conjunto de CPSIs del plan (multi-cuenta; un plan puede tener varios fondos).
		// Sin ellos, el backend devuelve legacy (sin filtro de plan).
		body: JSON.stringify({ productCodes })
	});

	if (!response.ok) {
		throw new Error('Network response was not ok');
	}

	const result = await response.json();
	return result.data;
};

export default getUserCostBasis;
